import useSWRInfinite from "swr/infinite"

import {
    DnoApplicationTypes,
    DnoEscalationStatuses,
} from "../../../../../constants/enums"
import {
    APPLICATION_PAGE_SIZE,
    ASSESSMENT_TYPE_KW,
    FIRST_PAGE,
    STATUS,
} from "../../../../../constants/generalConstants"
import { postClientProtected } from "../../../clients/clients"
import {
    IApplicationSummary,
    IApplications,
    dnoApplicationsMapper,
} from "../../../mappers/dnoInterface/dnoApplicationsMapper"
import { APPLICATION_LIST_CONFIG } from "../../../swrConfig"
import { DNO_LIST_APPLICATIONS } from "../../../urls/hybrid"

type TGetDnoApplications = {
    data: IApplicationSummary[] | undefined
    isLoading: boolean
    isError: boolean
    isValidating: boolean
    downloadData:
        | {
              results: unknown[]
          }
        | undefined
    page: number
    isFinalPage: boolean
    setPage: (
        size: number | ((_size: number) => number)
    ) => Promise<IApplications[] | undefined>
    // isFinalPage: (pageIndex: number, totalResults: number) => boolean
}

/**
 * Hook for listing DNO applications to review.
 *
 * Download data is for use in download button only. It has not been sanitised
 * and is unsuitable for use in UI.
 * @param {URLSearchParams} searchParams - denotes escalations vs notifications
 * @param {string} isEscalations - URL search params with query information
 * @returns {TGetDnoApplications} - SWR data, loading and error states.
 */
const useGetDnoApplications = (
    searchParams: URLSearchParams,
    isEscalations: boolean,
    sortKey: string
): TGetDnoApplications => {
    const searchParamsLocal = new URLSearchParams(searchParams.toString())
    searchParamsLocal.set(
        ASSESSMENT_TYPE_KW,
        isEscalations
            ? DnoApplicationTypes.all
            : DnoApplicationTypes.allApprovedIncCx
    )
    if (isEscalations && !searchParams.has(STATUS)) {
        Object.values(DnoEscalationStatuses).forEach((status) => {
            searchParamsLocal.append(STATUS, status)
        })
    }

    // interface for ouput of getKey function
    type TGetKey = {
        url: string
        nextPageKey: string | Record<string, string>
        pageIndex: number
    }

    /**
     * Injects key into useSWRInfinite hook. Key is an object with API URL
     * and pagination body.
     * @param {number} pageIndex - page number
     * @param {IApplications} previousPageData - previous page data
     * @returns {TGetKey | null } - object with API URL and pagination body
     */
    const getKey = (
        pageIndex: number,
        previousPageData: IApplications
    ): TGetKey | null => {
        const url = DNO_LIST_APPLICATIONS(
            searchParamsLocal.toString(),
            APPLICATION_PAGE_SIZE,
            sortKey
        )

        if (!previousPageData)
            return { url, pageIndex, nextPageKey: FIRST_PAGE }
        if (previousPageData && !previousPageData.nextPageKey) return null // reached the end
        return { url, pageIndex, nextPageKey: previousPageData.nextPageKey }
    }

    const {
        data: pageResults,
        error,
        isLoading,
        isValidating,
        size,
        setSize,
    } = useSWRInfinite(
        getKey,
        async (key: TGetKey): Promise<IApplications> => {
            try {
                // Extract pagination body
                let body
                if (typeof key?.nextPageKey === "string") body = null
                else body = key?.nextPageKey

                const res = await postClientProtected(key?.url, body)
                return dnoApplicationsMapper.mapList(res)
            } catch {
                throw Error()
            }
        },
        APPLICATION_LIST_CONFIG
    )
    // return true if any item in allPageResults has a null value for nextPageKey
    const isFinalPage = pageResults
        ? pageResults.some((page) => !page.nextPageKey)
        : false

    // Merge new data into existing paginated data
    // To do - extract UI
    const allPageResults = pageResults
        ? pageResults?.reduce(
              (acc: IApplicationSummary[], pageResult: IApplications) => [
                  ...acc,
                  ...pageResult.data,
              ],
              []
          )
        : undefined

    const downloadData = pageResults
        ? pageResults.reduce(
              (acc: unknown[], pageResult: IApplications) => [
                  ...acc,
                  ...pageResult.downloadData,
              ],
              []
          )
        : []

    return {
        data: allPageResults,
        downloadData: { results: downloadData },
        isLoading,
        isError: error,
        isValidating,
        page: size,
        isFinalPage,
        setPage: setSize,
    }
}

export default useGetDnoApplications
